import React, { useState, useMemo } from 'react';
import { parseISO, format } from 'date-fns';
import { TimeGrid } from '../calendar/CalendarGrid';
import { ChevronLeft, ChevronRight, Star, Clock, Users, BarChart3, Layers } from 'lucide-react';
import { cn } from '../../lib/utils';
import useCalendarStore from '../../store/useCalendarStore';

/**
 * DayScheduleViewer — Embeds CalendarGrid TimeGrid for pipeline preview steps.
 * Shows a day-view with generated events, scores sidebar, and schedule navigation.
 *
 * Props:
 * - schedules: Array of { date, events, scores, status, dropped, solver_stats }
 * - faculty: Array of { id, name, ... }
 * - batches: Array of { id, name, ... }
 * - onSelectSchedule: (index) => void — called when schedule index changes
 * - highlights: Optional availability highlight intervals
 * - readOnly: boolean — disables drag interactions
 */

const FACULTY_COLORS = [
    '#4285f4', '#7c3aed', '#059669', '#ea580c', '#e11d48',
    '#d97706', '#0d9488', '#4f46e5', '#db2777', '#64748b',
    '#2563eb', '#9333ea', '#16a34a', '#f97316', '#dc2626',
];

function getFacultyColor(facultyId, allFacultyIds) {
    const idx = allFacultyIds.indexOf(String(facultyId));
    return FACULTY_COLORS[idx >= 0 ? idx % FACULTY_COLORS.length : 0];
}

export default function DayScheduleViewer({
    schedules = [],
    faculty = [],
    batches = [],
    onSelectSchedule,
    highlights,
    readOnly = true,
}) {
    const [currentIndex, setCurrentIndex] = useState(0);

    const schedule = schedules[currentIndex] || null;
    const totalSchedules = schedules.length;

    // All faculty IDs for consistent coloring
    const allFacultyIds = useMemo(() => faculty.map(f => String(f.id)), [faculty]);

    // Read working hours from global settings store
    const settings = useCalendarStore(s => s.settings);
    const gridStartMinutes = (() => {
        const wh = settings?.working_hours_start || '08:00';
        const [h, m] = wh.split(':').map(Number);
        return h * 60 + (m || 0);
    })();

    // Convert solver events to TimeGrid-compatible format
    const timeGridEvents = useMemo(() => {
        if (!schedule?.events) return [];
        return schedule.events.map((ev, i) => {
            const dateStr = schedule.date || new Date().toISOString().split('T')[0];

            // Use solver-provided time strings (e.g. "07:00", "08:00")
            // Fall back to slot-based calculation with working hours from settings
            let start, end;
            if (ev.start_time && ev.end_time && typeof ev.start_time === 'string') {
                start = new Date(`${dateStr}T${ev.start_time}:00`);
                end = new Date(`${dateStr}T${ev.end_time}:00`);
            } else {
                const startSlot = ev.start_slot ?? 0;
                const durationSlots = ev.duration_slots ?? 4;
                const startMinTotal = gridStartMinutes + startSlot * 15;
                const endMinTotal = gridStartMinutes + (startSlot + durationSlots) * 15;
                start = new Date(`${dateStr}T${String(Math.floor(startMinTotal / 60)).padStart(2, '0')}:${String(startMinTotal % 60).padStart(2, '0')}:00`);
                end = new Date(`${dateStr}T${String(Math.floor(endMinTotal / 60)).padStart(2, '0')}:${String(endMinTotal % 60).padStart(2, '0')}:00`);
            }

            const facultyColor = getFacultyColor(ev.faculty_id, allFacultyIds);

            return {
                id: ev.assignment_id || `gen-${i}`,
                title: ev.subject_name || ev.title || `Event ${i + 1}`,
                start_time: start,
                end_time: end,
                event_type: 'scheduled_event',
                color: ev.is_fixed ? 'rose' : undefined,
                _customBg: ev.is_fixed ? undefined : facultyColor,
                faculty_id: ev.faculty_id,
                batch_id: ev.batch_id,
                score: ev.score,
            };
        });
    }, [schedule, allFacultyIds]);

    // Day array for TimeGrid (single day)
    const days = useMemo(() => {
        if (!schedule?.date) return [new Date()];
        return [parseISO(schedule.date)];
    }, [schedule]);

    const handlePrev = () => {
        const newIdx = Math.max(0, currentIndex - 1);
        setCurrentIndex(newIdx);
        onSelectSchedule?.(newIdx);
    };

    const handleNext = () => {
        const newIdx = Math.min(totalSchedules - 1, currentIndex + 1);
        setCurrentIndex(newIdx);
        onSelectSchedule?.(newIdx);
    };

    if (!schedule) {
        return (
            <div className="text-center py-8 text-zinc-400 text-sm">
                No schedules generated yet.
            </div>
        );
    }

    const scores = schedule.scores || {};
    const stats = schedule.solver_stats || {};

    return (
        <div className="space-y-3">
            {/* Schedule Navigator */}
            {totalSchedules > 1 && (
                <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700">
                    <button
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-30 transition-colors"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">
                        Schedule {currentIndex + 1} / {totalSchedules}
                        <span className="ml-2 text-zinc-400">
                            {schedule.date && format(parseISO(schedule.date), 'EEE, MMM d')}
                        </span>
                    </span>
                    <button
                        onClick={handleNext}
                        disabled={currentIndex === totalSchedules - 1}
                        className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-30 transition-colors"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Scores Summary Bar */}
            <div className="grid grid-cols-4 gap-2">
                <ScoreCard
                    icon={<BarChart3 className="w-3.5 h-3.5" />}
                    label="Coverage"
                    value={scores.coverage_pct != null ? `${Math.round(scores.coverage_pct)}%` : '—'}
                    color="emerald"
                />
                <ScoreCard
                    icon={<Star className="w-3.5 h-3.5" />}
                    label="Preference"
                    value={scores.preference_satisfaction != null ? `${Math.round(scores.preference_satisfaction)}%` : '—'}
                    color="amber"
                />
                <ScoreCard
                    icon={<Users className="w-3.5 h-3.5" />}
                    label="Faculty Use"
                    value={scores.faculty_utilization != null ? `${Math.round(scores.faculty_utilization)}%` : '—'}
                    color="blue"
                />
                <ScoreCard
                    icon={<Layers className="w-3.5 h-3.5" />}
                    label="Compactness"
                    value={scores.compactness_index != null ? `${Math.round(scores.compactness_index)}%` : '—'}
                    color="violet"
                />
            </div>

            {/* Status + Solver Stats */}
            <div className="flex items-center justify-between text-[0.65rem] text-zinc-400 px-1">
                <span className={cn(
                    "font-semibold uppercase tracking-wide",
                    schedule.status === 'optimal' ? 'text-emerald-500' :
                        schedule.status === 'feasible' ? 'text-blue-500' :
                            schedule.status === 'partial' ? 'text-amber-500' : 'text-red-400'
                )}>
                    {schedule.status || 'unknown'}
                </span>
                <span className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {stats.time ? `${stats.time.toFixed(2)}s` : '—'}
                    {schedule.events && <span>· {schedule.events.length} events</span>}
                    {schedule.dropped?.length > 0 && (
                        <span className="text-red-400">· {schedule.dropped.length} dropped</span>
                    )}
                </span>
            </div>

            {/* Calendar Day View */}
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden"
                style={{ height: '400px', overflowY: 'auto' }}>
                <TimeGrid
                    events={timeGridEvents}
                    days={days}
                    onSlotClick={() => { }}
                    onEventClick={() => { }}
                    highlights={highlights}
                    faculty={faculty}
                    batches={batches}
                />
            </div>

            {/* Faculty Color Legend */}
            {schedule.events?.length > 0 && (
                <div className="flex flex-wrap gap-2 px-1">
                    {[...new Set(schedule.events.map(e => e.faculty_id))].filter(Boolean).map(fId => {
                        const f = faculty.find(fac => String(fac.id) === String(fId));
                        return (
                            <span key={fId} className="inline-flex items-center gap-1 text-[0.6rem] text-zinc-500">
                                <span
                                    className="w-2.5 h-2.5 rounded-full inline-block"
                                    style={{ backgroundColor: getFacultyColor(fId, allFacultyIds) }}
                                />
                                {f?.name || `Faculty ${fId}`}
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Dropped Events Warning */}
            {schedule.dropped?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                        ⚠ {schedule.dropped.length} assignment(s) could not be scheduled
                    </h5>
                    <ul className="text-[0.65rem] text-amber-600 dark:text-amber-400 space-y-0.5">
                        {schedule.dropped.slice(0, 5).map((d, i) => (
                            <li key={i}>· {d.subject_name || d.assignment_id || `Assignment ${i + 1}`} — {d.reason || 'no feasible slot'}</li>
                        ))}
                        {schedule.dropped.length > 5 && (
                            <li className="text-amber-400">...and {schedule.dropped.length - 5} more</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

function ScoreCard({ icon, label, value, color }) {
    const colorClasses = {
        emerald: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20',
        amber: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20',
        blue: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20',
        violet: 'text-violet-600 bg-violet-50 dark:text-violet-400 dark:bg-violet-900/20',
        red: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20',
    };

    return (
        <div className={cn("rounded-lg p-2 text-center", colorClasses[color] || colorClasses.blue)}>
            <div className="flex items-center justify-center gap-1 mb-0.5">
                {icon}
                <span className="text-[0.6rem] font-medium uppercase tracking-wide">{label}</span>
            </div>
            <div className="text-sm font-bold">{value}</div>
        </div>
    );
}
